import { Component, ViewChild } from '@angular/core';
import { LeaveTypeModel } from './model/leave-type.model';
import { LeaveTypeService } from './leave-type.service';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { RequestData, SearchSort } from 'src/app/common/models/request-data.model';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { FilterListItemModel, SearchFilterComponent, SearchParam } from 'src/app/common/components/search-filter/search-filter.component';
import { Router } from '@angular/router';
import { StateManagerService } from 'src/app/common/services/state.manager.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, SortDirection } from '@angular/material/sort';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-leave-type',
  templateUrl: './leave-type.component.html',
  styleUrls: ['./leave-type.component.scss'],
  standalone: false
})
export class LeaveTypeComponent {

  leaveTypeList: LeaveTypeModel[] = [];
  showSearch: boolean = false;
  isLoading = true;
  filter: FilterListItemModel[] = [
    { title: "Code", column: "code" },
    { title: "Name", column: "name" },
    { title: "Description", column: "description" },
    {
      title: "Is Active", column: "is_active", type: "boolean",
      values: [
        { title: "Yes", value: "1" },
        { title: "No", value: "0" },
      ]
    },
  ];
  displayedColumns = ['item', 'code', 'name', 'description', 'is_active', 'action'];
  pagingData = { length: 0, pageSize: 50, pageIndex: 0 };

  isHandset: boolean = false;
  searchColumFilter: SearchParam = {};
  columnSortData: SearchSort[] = [];
  selectedItem?: LeaveTypeModel;
  dataSource = new MatTableDataSource(this.leaveTypeList);
  permission: any;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchFilter') searchFilter!: SearchFilterComponent;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private leaveTypeService: LeaveTypeService,
    public dialog: MatDialog,
    private snackBarService: SnackBarService,
    public permissionService: PermissionService,
    private router: Router,
    private stateManagerService: StateManagerService
  ) {
    this.permission = permissionService.getPagePermission("leave-type");
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((state: BreakpointState) => {
      this.isHandset = state.matches;
    });
  }

  ngAfterViewInit(): void {
    this.loadPreviousState();
  }

  loadPreviousState(): void {
    var previousState = this.stateManagerService.getState(this.constructor.name);
    if (previousState !== undefined && this.searchFilter != undefined) {
      if (previousState.state.search.filters.scope) {
        this.pagingData.pageIndex = previousState.state.search.filters.scope.offset / previousState.state.search.filters.scope.limit;
        this.pagingData.pageSize = previousState.state.search.filters.scope.limit;
      }
      if (previousState.state.search.sort != undefined && previousState.state.search.sort.length > 0) {
        this.setSortColumn(previousState.state.search.sort[0].column, previousState.state.search.sort[0].order);
      }
      this.searchColumFilter = JSON.parse(JSON.stringify(previousState.state.search.filters));
      this.searchFilter.setSearchFilter(this.searchColumFilter, true);
    } else {
      this.setSortColumn('name', 'asc');
      this.loadData();
    }
  }

  setSortColumn(column: string, direction: SortDirection): void {
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: column, order: direction });
    if (this.sort) {
      this.sort.sort({ id: column, start: direction, disableClear: false });
    }
  }

  setDataSource(list: LeaveTypeModel[]) {
    this.dataSource = new MatTableDataSource(list);
    this.dataSource.sort = this.sort;
  }

  buildRequestData(): RequestData {
    let searchParam = this.searchColumFilter;
    let requestData: RequestData = {
      search: {
        filters: {
          simpleSearch: searchParam.freeSearch,
          advSearch: searchParam.advSearch || [],
          scope: {
            limit: this.pagingData.pageSize,
            offset: (this.pagingData.pageIndex * this.pagingData.pageSize),
          }
        },
        sort: this.columnSortData,
      }
    };
    return requestData;
  }

  onFilterApplied(columFilter: any): void {
    this.searchColumFilter = columFilter;
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;
    let requestData = this.buildRequestData();

    this.leaveTypeService.getLeaveTypeList(requestData).subscribe({
      next: (response) => {
        if (response.status == 'SUCCESS') {
          this.leaveTypeList = response.data.leave_types || [];
          this.setDataSource(this.leaveTypeList);
          this.pagingData.length = response.data.total_count;
          if (this.pagingData.length > 0) {
            this.stateManagerService.setState(this.constructor.name, requestData);
          }
        } else {
          this.snackBarService.openSnackBar({
            message: response.message,
            title: 'Failed!!!',
            type: 'error'
          });
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.snackBarService.openSnackBar({
          message: 'Connection to the server failed',
          title: 'Failed!!!',
          type: 'error'
        });
      }
    });
  }

  sortData($event: any): void {
    if (this.sort) {
      this.columnSortData = [{ column: this.sort.active, order: this.sort.direction }];
      this.loadData();
    }
  }

  onRowSelected(item: LeaveTypeModel): void {
    this.selectedItem = item;
  }

  onEdit(info: LeaveTypeModel): void {
    this.router.navigate(['system/leave-type-edit', info.id]);
  }

  onDelete(info: LeaveTypeModel): void {
    const dialogData = new ConfirmDialogModel(
      'Delete Confirmation',
      'Are you sure you want to delete the leave type <strong>' + info.name + '</strong>?'
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '400px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((dialogResult) => {
      if (dialogResult) {
        this.leaveTypeService.delete(info).subscribe({
          next: (response) => {
            if (response.status == 'SUCCESS') {
              this.snackBarService.openSnackBar({
                type: 'success',
                title: 'Deleted',
                message: 'The record for ' + info.name + " has been deleted successfully."
              });
              this.loadData();
            } else {
              this.snackBarService.openSnackBar({
                message: response.message,
                title: 'Failed!!!',
                type: 'error'
              });
            }
          },
          error: (error) => {
            this.snackBarService.openSnackBar({
              message: 'Failed to delete. Connection to the server failed',
              title: 'Failed!!!',
              type: 'error'
            });
          }
        });
      }
    });
  }

  onePageEvent(event: any): void {
    this.pagingData.pageIndex = event.pageIndex;
    this.pagingData.pageSize = event.pageSize;
    this.loadData();
  }

  addClicked(): void {
    this.router.navigate(['system/leave-type-edit', 0]);
  }
}
